// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { Link } from '@/components/link';
import { OpenInNew, SearchOutlined } from '@/constants/hardfin-standard-icons';
import { MemberOnDay } from '@/membership/utilities/membership-days';
import {
  Box,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';

// Three across, so a serial and its location fit on one row without wrapping.
const COLUMNS_PER_ROW = 3;

// Past five rows the grid scrolls itself rather than the page. A cell is a
// serial over its location, and a long location truncates rather than wrapping,
// so every row is the same height.
const MAX_ROWS = 5;
const ROW_HEIGHT = 60;
const ROW_GAP = 8;

interface Props {
  members: MemberOnDay[];
}

/**
 * The serials on site on the scrubbed day, one tab per SKU over a search that
 * spans every tab.
 */
export function MembersSerialsGrid(props: Props) {
  const [searchQuery, setSearchQuery] = useState('');
  const [chosenSku, setChosenSku] = useState<string | null>(null);

  // Tabs come from the whole day rather than the search, so a SKU does not
  // vanish mid-search and take the tab under the cursor with it.
  const skus = useMemo(() => toSkus(props.members), [props.members]);

  const found = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    if (term.length === 0) return props.members;
    return props.members.filter(
      (member) =>
        member.serialNumber.toLowerCase().includes(term) ||
        member.locationName.toLowerCase().includes(term)
    );
  }, [props.members, searchQuery]);

  // The chosen tab holds unless the search empties it, then the first tab with
  // something in it takes over, so a search never lands on a blank grid.
  const countsBySku = toCountsBySku(found);
  const hasChosenResults =
    chosenSku !== null && (countsBySku[chosenSku] ?? 0) > 0;
  const activeSku = hasChosenResults
    ? chosenSku
    : (skus.find((sku) => (countsBySku[sku] ?? 0) > 0) ?? skus.at(0) ?? '');

  const shown = found.filter((member) => member.itemSku === activeSku);
  // A day can hold thousands of serials, so the grid is windowed by row. The
  // rows are chunked here because react-window counts rows, not cells.
  const rows = useMemo(() => toRows(shown), [shown]);

  return (
    <Stack gap={2}>
      <TextField
        fullWidth
        size="small"
        placeholder="Search serial or location"
        value={searchQuery}
        onChange={(event) => {
          setSearchQuery(event.target.value);
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchOutlined />
            </InputAdornment>
          ),
        }}
      />

      <Tabs
        value={activeSku}
        onChange={(_event, nextSku: string) => {
          setChosenSku(nextSku);
        }}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 0 }}
      >
        {skus.map((sku) => (
          <Tab
            key={sku}
            value={sku}
            label={`${sku} (${String(countsBySku[sku] ?? 0)})`}
            sx={{ minHeight: 0, py: 1 }}
          />
        ))}
      </Tabs>

      {shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
          {props.members.length === 0
            ? 'No members on this day'
            : 'No assets match that search'}
        </Typography>
      ) : (
        <FixedSizeList
          height={
            Math.min(rows.length, MAX_ROWS) * (ROW_HEIGHT + ROW_GAP) - ROW_GAP
          }
          width="100%"
          itemSize={ROW_HEIGHT + ROW_GAP}
          itemCount={rows.length}
          itemData={rows}
          overscanCount={4}
        >
          {SerialRow}
        </FixedSizeList>
      )}
    </Stack>
  );
}

function toSkus(members: MemberOnDay[]): string[] {
  return [...new Set(members.map((member) => member.itemSku))].sort((a, b) =>
    a.localeCompare(b)
  );
}

function toCountsBySku(members: MemberOnDay[]): Record<string, number> {
  const out: Record<string, number> = {};
  members.forEach((member) => {
    out[member.itemSku] = (out[member.itemSku] ?? 0) + 1;
  });
  return out;
}

function toRows(members: MemberOnDay[]): MemberOnDay[][] {
  const out: MemberOnDay[][] = [];
  for (let i = 0; i < members.length; i += COLUMNS_PER_ROW) {
    out.push(members.slice(i, i + COLUMNS_PER_ROW));
  }
  return out;
}

// React-window takes this as a component type, so an inline arrow would give
// every row a new type on each render and remount the whole visible window.
function SerialRow(props: ListChildComponentProps<MemberOnDay[][]>) {
  return (
    <Box style={props.style} sx={{ display: 'flex', gap: 1, pr: 1 }}>
      {props.data[props.index].map((member) => (
        <Box
          key={member.key}
          sx={{ flex: `1 1 ${100 / COLUMNS_PER_ROW}%`, minWidth: 0 }}
        >
          <Paper variant="outlined" sx={{ p: 1, minWidth: 0 }}>
            <Link
              to="/assets/$assetKey"
              params={{ assetKey: member.key }}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <Typography variant="body2" component="span">
                {member.serialNumber}
              </Typography>
              <OpenInNew sx={{ fontSize: 14 }} />
            </Link>
            <Box
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                {member.locationName}
              </Typography>
            </Box>
          </Paper>
        </Box>
      ))}
    </Box>
  );
}
